import type { MediaItem, MediaType } from '../types/movie';
import { MovieCard } from './MovieCard';
import { Loader2 } from 'lucide-react';

interface MovieGridProps {
  movies: MediaItem[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onMovieClick?: (movie: MediaItem) => void;
  mediaType?: MediaType;
}

export function MovieGrid({
  movies,
  loading,
  hasMore,
  onLoadMore,
  onMovieClick,
  mediaType = 'movie',
}: MovieGridProps) {
  const isTV = mediaType === 'tv';

  if (!loading && movies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <div className="text-6xl mb-4">{isTV ? '📺' : '🎬'}</div>
        <h3 className="text-xl font-semibold mb-2">No {isTV ? 'TV shows' : 'movies'} found</h3>
        <p className="text-sm text-slate-500">
          Try adjusting your filters to find more results
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4">
        {movies.map((movie) => (
          <MovieCard key={movie.id} movie={movie} onClick={onMovieClick} />
        ))}

        {/* Loading skeletons */}
        {loading &&
          Array.from({ length: 14 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="group">
              <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-slate-800">
                <div className="w-full h-full skeleton" />
              </div>
              <div className="mt-2 px-1 space-y-1">
                <div className="h-4 skeleton rounded w-3/4" />
              </div>
            </div>
          ))}
      </div>

      {/* Load more button */}
      {hasMore && !loading && (
        <div className="flex justify-center">
          <button
            onClick={onLoadMore}
            className={`px-8 py-3 text-white font-semibold rounded-full transition-all shadow-lg ${
              isTV
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-purple-500/25'
                : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-cyan-500/25'
            }`}
          >
            Load More {isTV ? 'TV Shows' : 'Movies'}
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {loading && movies.length > 0 && (
        <div className="flex justify-center py-4">
          <Loader2 className={`w-8 h-8 animate-spin ${isTV ? 'text-purple-500' : 'text-cyan-500'}`} />
        </div>
      )}
    </div>
  );
}
